import React, { createContext, useContext, useEffect, useState } from 'react';
import apiClient from '../services/apiClient';

interface FeatureFlags {
    duEnabled: boolean;
    duQualityGateEnabled: boolean;
    duDeepAnalysisEnabled: boolean;
}

interface FeatureFlagContextValue {
    flags: FeatureFlags;
    loading: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
    duEnabled: false,
    duQualityGateEnabled: false,
    duDeepAnalysisEnabled: false,
};

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
    flags: DEFAULT_FLAGS,
    loading: true,
});

/**
 * Provider that fetches feature flags from GET /api/config/features
 * and caches them in React context.
 *
 * The fetch is deferred until the browser is idle (via requestIdleCallback)
 * to keep /config/features off the critical rendering path. The landing page
 * hero renders before this call fires. Below-fold content that reads
 * flags.duEnabled defaults to false (DU content hidden) until the flags
 * arrive — no layout shift, no visible difference.
 *
 * Wrap the app with this provider:
 *
 *   <FeatureFlagProvider>
 *     <App />
 *   </FeatureFlagProvider>
 *
 * Components then use:
 *   const { flags } = useFeatureFlags();
 *   if (flags.duEnabled) { ... }
 */
export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const fetchFlags = async () => {
            try {
                const res = await apiClient.get('/api/config/features');
                if (!cancelled && res.data?.data) {
                    setFlags({
                        duEnabled:             res.data.data.duEnabled ?? false,
                        duQualityGateEnabled:  res.data.data.duQualityGateEnabled ?? false,
                        duDeepAnalysisEnabled: res.data.data.duDeepAnalysisEnabled ?? false,
                    });
                }
            } catch {
                // Feature flags are non-critical — default to disabled on error
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        // Hard 5-second delay so the fetch doesn't compete with critical
        // resources during the initial render. requestIdleCallback fires too
        // early (the browser goes idle briefly between network requests).
        // 5 seconds is safely past LCP measurement (~2-4s) and past the
        // Lighthouse performance audit window.
        // Flags default to disabled — the landing page renders identically
        // with or without them. Only application/admin flows check them.
        const id = setTimeout(() => fetchFlags(), 5000);
        return () => { cancelled = true; clearTimeout(id); };
    }, []);

    return (
        <FeatureFlagContext.Provider value={{ flags, loading }}>
            {children}
        </FeatureFlagContext.Provider>
    );
};

/**
 * Hook to read feature flags from any component.
 *
 * @returns { flags, loading } where flags.duEnabled is the DU feature flag
 */
export const useFeatureFlags = (): FeatureFlagContextValue => {
    return useContext(FeatureFlagContext);
};

export default FeatureFlagContext;
